import { stripTypename } from '@apollo/client/utilities';
import isEqual from 'lodash/isEqual';

import { DiagramDirection, type DiagramInput } from '@/types/schema';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';

export const DIAGRAM_SCHEMA_VERSION = 2;

/**
 * The name and title of a diagram before it has a name. It must match
 * `defaultName` in the Platform `diagramservice` package. The backend
 * replaces this name with the generated title when the diagram builder
 * finishes, unless the user renamed the diagram.
 */
export const DEFAULT_DIAGRAM_NAME = 'Untitled diagram';

type DiagramContent = DiagramBuilder_DiagramFragment['content'];
export type DiagramBuilderNode = DiagramContent['nodes'][number];
export type DiagramBuilderEdge = DiagramContent['edges'][number];
export type DiagramBuilderStage = DiagramContent['stages'][number];
export type DiagramBuilderNodeStyle = NonNullable<
  DiagramBuilderNode['nodeStyle']
>;
export type DiagramBuilderEdgeStyle = NonNullable<
  DiagramBuilderEdge['edgeStyle']
>;

/**
 * Returns the diagram as mutation input. Apollo adds `__typename` to query
 * results, and GraphQL input types reject it.
 */
export function diagramInput(
  diagram: DiagramBuilder_DiagramFragment
): DiagramInput {
  return stripTypename(diagram);
}

/** True when the diagrams have the same content. Ignores `__typename`. */
export function isSameDiagram(
  a: DiagramBuilder_DiagramFragment | null,
  b: DiagramBuilder_DiagramFragment | null
) {
  return isEqual(a && stripTypename(a), b && stripTypename(b));
}

export function emptyDiagram(): DiagramBuilder_DiagramFragment {
  return {
    schemaVersion: DIAGRAM_SCHEMA_VERSION,
    content: {
      title: DEFAULT_DIAGRAM_NAME,
      direction: DiagramDirection.TopToBottom,
      nodes: [],
      edges: [],
      stages: [],
    },
  };
}
